/**
 * One keybinding, as a row of t3code's four-column table.
 *
 * Command / Keybinding / When / Status, with the tracks declared once in
 * `KEYBINDING_ROW_COLUMNS` and shared with the header so a cell and its label
 * cannot drift apart. Peers are separated by the zebra fill and the hover
 * plane, which is why the row carries no rule of its own.
 *
 * @module
 */
import type { KeybindingCommand, KeybindingWhenNode } from "@cmmd-forge/contracts";
import { EllipsisIcon, TriangleAlertIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "../ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { KeybindingShortcutRecorder } from "./KeybindingShortcutRecorder";
import { whenAstToExpression } from "./keybindingWhenDraft";
import { commandLabel, type KeybindingRow } from "./KeybindingsSettings.logic";
import { WhenExpressionBuilder } from "./WhenExpressionBuilder";

/**
 * The four tracks, shared by the header and every row.
 *
 * Fixed minimums rather than content sizing: each row is its own grid, so
 * `auto` tracks would size to that row's own text and the columns would step
 * in and out down the page.
 */
export const KEYBINDING_ROW_COLUMNS =
  "grid-cols-[minmax(190px,1.1fr)_minmax(220px,0.85fr)_minmax(210px,1fr)_60px]";

/** Zebra fill and hover plane, in place of a rule between rows. */
export const KEYBINDING_ROW_CLASSNAME =
  "grid items-center px-4 py-1.5 text-sm even:bg-muted/15 hover:bg-accent/40";

export interface KeybindingTableRowProps {
  readonly row: KeybindingRow;
  readonly variables: ReadonlyArray<string>;
  readonly isSaving: boolean;
  readonly isRecording: boolean;
  readonly onStartRecording: () => void;
  readonly onCancelRecording: () => void;
  readonly onRebind: (shortcut: string) => void;
  readonly onChangeWhen: (when: string | undefined) => void;
  readonly onReset: () => void;
  readonly onRemove: () => void;
  readonly platform?: string | undefined;
}

/** The shared warning for a chord that answers to more than one command here. */
export function KeybindingConflictWarning({
  conflicts,
}: {
  readonly conflicts: ReadonlyArray<KeybindingCommand>;
}) {
  if (conflicts.length === 0) return null;
  const labels = conflicts.map((command) => commandLabel(command)).join(", ");
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            className="inline-flex size-4.5 shrink-0 items-center justify-center rounded-sm text-warning-foreground"
            aria-label={`Also bound to ${labels}`}
          >
            <TriangleAlertIcon className="size-3.5" />
          </span>
        }
      />
      <TooltipPopup side="top" className="max-w-72 whitespace-normal leading-relaxed">
        Also bound to {labels}
      </TooltipPopup>
    </Tooltip>
  );
}

/** The When cell: the clause as text, opening the editor. */
function WhenCell({
  row,
  variables,
  isSaving,
  onChangeWhen,
}: {
  readonly row: KeybindingRow;
  readonly variables: ReadonlyArray<string>;
  readonly isSaving: boolean;
  readonly onChangeWhen: (when: string | undefined) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<KeybindingWhenNode | undefined>(undefined);
  const [isValid, setIsValid] = useState(true);
  const readable = whenAstToExpression(row.whenAst);

  // The draft is seeded when the popover opens, not held across closes: the row
  // re-renders from the server's copy after a save, and a stale draft would
  // quietly re-apply the previous clause the next time it was opened.
  const open = (next: boolean) => {
    if (next) {
      setDraft(row.whenAst);
      setIsValid(true);
    }
    setIsOpen(next);
  };

  return (
    <Popover onOpenChange={open} open={isOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            disabled={isSaving}
            aria-label={`Edit when clause for ${commandLabel(row.command)}`}
            className="inline-flex h-7 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-2.5 text-left font-mono text-foreground text-xs outline-none transition-colors hover:bg-accent focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/24"
          />
        }
      >
        {/* `whenAstToExpression`, not `row.when`. The row's `when` is shared's
            canonical form, which parenthesises unconditionally and so renders
            `!terminalFocus` as `!(terminalFocus)` -- correct for comparing two
            rules, unreadable as the contents of a column. */}
        <span className={readable === "" ? "truncate text-muted-foreground" : "truncate"}>
          {readable === "" ? "Always" : readable}
        </span>
      </PopoverTrigger>
      <PopoverPopup align="start" className="w-96 max-w-[calc(100vw-2rem)] p-3">
        <WhenExpressionBuilder
          onChange={setDraft}
          onValidityChange={setIsValid}
          value={draft}
          variables={variables}
        />
        <div className="mt-3 flex justify-end gap-2">
          <Button onClick={() => setIsOpen(false)} size="xs" type="button" variant="ghost">
            Cancel
          </Button>
          <Button
            // Refused while the text field holds something half-typed: the
            // clause is sent as source text and would fail at the server.
            disabled={!isValid || isSaving}
            onClick={() => {
              onChangeWhen(draft === undefined ? undefined : whenAstToExpression(draft));
              setIsOpen(false);
            }}
            size="xs"
            type="button"
          >
            Save
          </Button>
        </div>
      </PopoverPopup>
    </Popover>
  );
}

export function KeybindingTableRow({
  row,
  variables,
  isSaving,
  isRecording,
  onStartRecording,
  onCancelRecording,
  onRebind,
  onChangeWhen,
  onReset,
  onRemove,
  platform,
}: KeybindingTableRowProps) {
  // Reset restores a shipped default; Remove drops the rule outright. A row
  // with no default has nothing to reset *to*, so offering both would give one
  // of them no meaning.
  const canReset = row.source === "Custom" && row.defaultKeys.length > 0;
  const canRemove = row.source === "Custom";

  return (
    <li className={`${KEYBINDING_ROW_CLASSNAME} ${KEYBINDING_ROW_COLUMNS}`}>
      <div className="min-w-0 pr-4">
        <Tooltip>
          <TooltipTrigger
            render={
              <div
                aria-label={row.command}
                className="truncate font-medium text-[13px] text-foreground"
              />
            }
          >
            {commandLabel(row.command)}
          </TooltipTrigger>
          <TooltipPopup side="top">{row.command}</TooltipPopup>
        </Tooltip>
      </div>

      <div className="flex min-w-0 items-center gap-2 pr-4">
        <KeybindingShortcutRecorder
          isRecording={isRecording}
          onCancelRecording={onCancelRecording}
          onRecorded={onRebind}
          onStartRecording={onStartRecording}
          platform={platform}
          row={row}
        />
      </div>

      <div className="pr-4">
        <WhenCell isSaving={isSaving} onChangeWhen={onChangeWhen} row={row} variables={variables} />
      </div>

      <div className="flex items-center justify-end gap-1">
        <KeybindingConflictWarning conflicts={row.conflicts} />
        {canReset || canRemove ? (
          <Menu>
            <MenuTrigger
              render={
                <Button
                  aria-label={`Actions for ${commandLabel(row.command)}`}
                  className="size-7 text-muted-foreground hover:text-foreground"
                  disabled={isSaving}
                  size="icon-sm"
                  type="button"
                  variant="ghost"
                />
              }
            >
              <EllipsisIcon className="size-3.5" />
            </MenuTrigger>
            <MenuPopup align="end" className="min-w-36">
              {canReset ? (
                <MenuItem disabled={isSaving} onClick={onReset}>
                  Reset to default
                </MenuItem>
              ) : null}
              {canRemove ? (
                <MenuItem disabled={isSaving} onClick={onRemove} variant="destructive">
                  Remove
                </MenuItem>
              ) : null}
            </MenuPopup>
          </Menu>
        ) : null}
      </div>
    </li>
  );
}
